import { useState, useEffect, useMemo } from 'react';
import { inventoryAPI, uploadAPI, notificationsAPI, clientsAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { resolveImageUrl } from '@/lib/imageUtils';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search as SearchIcon, Loader2, Upload, Bell, Send, FileText, X, MessageCircle, Mail, Filter, Eye, QrCode, Printer, Download, Sparkles, MoreVertical, Image, FolderOpen, ChevronLeft, ChevronRight } from 'lucide-react';
import QRCode from 'react-qr-code';
import DOMPurify from 'dompurify';
import { BulkSelectCheckbox, BulkActionBar } from '@/components/BulkActions';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';

const formatPrice = (p) => p ? `$${p.toLocaleString()}` : '--';

const emptyForm = {
  type: 'truck', vin: '', make: '', model: '', year: '', mileage_km: '', hours: '', has_hours: false, price: '',
  body_type: '', condition: 'Used', status: 'Available', featured: false, price_public: false,
  engine: '', horsepower: '', transmission: '', rear_ratio: '', axles: '',
  wheelbase: '', suspension: '', gvwr: '', trailer_type: '', trailer_length: '',
  axle_count: '', roof_type: '', door_type: '', reefer_details: '',
  sleeper_daycab: '', color: '', floor_type: '', width: '', length: '',
  reefer_unit: '', reefer_hours: '',
  description: '', title: '', location: 'Mississauga, ON', images: [], video_url: '',
};

const EQUIPMENT_TYPES = [
  { value: 'truck', label: 'Truck' },
  { value: 'trailer', label: 'Trailer' },
  { value: 'dry_van', label: 'Dry Van' },
  { value: 'reefer', label: 'Reefer' },
  { value: 'flatbed', label: 'Flatbed' },
  { value: 'other', label: 'Other' },
];

const TYPE_LABELS = Object.fromEntries(EQUIPMENT_TYPES.map(t => [t.value, t.label]));

const DOC_TYPES = [
  { value: 'quote', label: 'Quote' },
];

const PROVINCE_TAX = [
  { code: 'ON', name: 'Ontario', rate: 13 },
  { code: 'AB', name: 'Alberta', rate: 5 },
  { code: 'BC', name: 'British Columbia', rate: 12 },
  { code: 'QC', name: 'Quebec', rate: 14.975 },
  { code: 'SK', name: 'Saskatchewan', rate: 11 },
  { code: 'MB', name: 'Manitoba', rate: 12 },
  { code: 'NS', name: 'Nova Scotia', rate: 15 },
  { code: 'NB', name: 'New Brunswick', rate: 15 },
  { code: 'PE', name: 'PEI', rate: 15 },
  { code: 'NL', name: 'Newfoundland', rate: 15 },
  { code: 'NT', name: 'NWT', rate: 5 },
  { code: 'NU', name: 'Nunavut', rate: 5 },
  { code: 'YT', name: 'Yukon', rate: 5 },
];

export default function InventoryManagement() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [decoding, setDecoding] = useState(false);
  const [search, setSearch] = useState('');

  // Filters
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [makeFilter, setMakeFilter] = useState('all');

  // Custom statuses
  const [statuses, setStatuses] = useState(['Available', 'Hold', 'Sold', 'Not Ready to Sell', 'Coming Soon']);
  const [customStatusInput, setCustomStatusInput] = useState('');

  // Send Listing dialog
  const [sendListingOpen, setSendListingOpen] = useState(false);
  const [sendListingItem, setSendListingItem] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [customEmails, setCustomEmails] = useState('');
  const [sendChannels, setSendChannels] = useState(['email']);
  const [clientSearch, setClientSearch] = useState('');
  const [sendingListing, setSendingListing] = useState(false);

  // Send Document dialog
  const [sendDocOpen, setSendDocOpen] = useState(false);
  const [sendDocItem, setSendDocItem] = useState(null);
  const [docType, setDocType] = useState('quote');
  const [docClientId, setDocClientId] = useState('');
  const [docCustomEmail, setDocCustomEmail] = useState('');
  const [docNotes, setDocNotes] = useState('');
  const [docQuantity, setDocQuantity] = useState('1');
  const [docUnitPrice, setDocUnitPrice] = useState('');
  const [docSafetyCertified, setDocSafetyCertified] = useState(false);
  const [docValidity, setDocValidity] = useState('7 days');
  const [docAdminFee, setDocAdminFee] = useState('');
  const [docTaxProvince, setDocTaxProvince] = useState('ON');
  const [docTaxRate, setDocTaxRate] = useState('13');
  const [docHoldDeposit, setDocHoldDeposit] = useState('');
  const [sendingDoc, setSendingDoc] = useState(false);

  // Multi-Unit Quote dialog
  const [multiQuoteOpen, setMultiQuoteOpen] = useState(false);
  const [multiQuoteClientId, setMultiQuoteClientId] = useState('');
  const [multiQuoteEmail, setMultiQuoteEmail] = useState('');
  const [multiQuoteNotes, setMultiQuoteNotes] = useState('');
  const [multiQuotePrices, setMultiQuotePrices] = useState({});
  const [multiQuoteAdminFee, setMultiQuoteAdminFee] = useState('');
  const [multiQuoteHst, setMultiQuoteHst] = useState('ON');
  const [multiQuoteHstRate, setMultiQuoteHstRate] = useState('13');
  const [multiQuoteHoldDeposit, setMultiQuoteHoldDeposit] = useState('');
  const [sendingMultiQuote, setSendingMultiQuote] = useState(false);

  // QR Sticker dialog
  const [qrItem, setQrItem] = useState(null);

  // Photos lightbox
  const [photosItem, setPhotosItem] = useState(null);
  const [photoIdx, setPhotoIdx] = useState(0);

  // Inventory documents dialog
  const [docsDialogItem, setDocsDialogItem] = useState(null);
  const [invDocs, setInvDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [uploadingInvDoc, setUploadingInvDoc] = useState(false);
  const [invDocType, setInvDocType] = useState('warranty');

  // Bulk selection
  const [selected, setSelected] = useState([]);
  const toggleSelect = (id) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleAll = () => setSelected(p => p.length === filteredItems.length ? [] : filteredItems.map(i => i.id));
  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.length} item(s)?`)) return;
    try { await inventoryAPI.bulkDelete(selected); toast.success(`${selected.length} item(s) deleted`); setSelected([]); load(); } catch { toast.error('Failed'); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await inventoryAPI.list({ limit: 100, search: search || undefined });
      setItems(r.data.items || []);
    } catch (e) { console.error('Failed to load statuses:', e); }
    setLoading(false);
  };

  const loadStatuses = async () => {
    try {
      const r = await inventoryAPI.getStatuses();
      setStatuses(r.data.statuses || ['Available', 'Hold', 'Sold', 'Not Ready to Sell', 'Coming Soon']);
    } catch (e) { console.error('Failed to load statuses:', e); }
  };

  const loadClients = async () => {
    try {
      const r = await clientsAPI.list();
      setClients(r.data || []);
    } catch (e) { console.error('Failed to load statuses:', e); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadStatuses(); loadClients(); }, []);

  // Filtered items based on type, status, and make filters
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      if (typeFilter !== 'all' && item.type !== typeFilter) return false;
      if (statusFilter !== 'all' && item.status !== statusFilter) return false;
      if (makeFilter !== 'all' && item.make !== makeFilter) return false;
      return true;
    });
  }, [items, typeFilter, statusFilter, makeFilter]);

  const openNew = () => { setForm(emptyForm); setEditId(null); setDialogOpen(true); };
  const openEdit = (item) => {
    setForm({
      ...emptyForm,
      ...item,
      year: item.year?.toString() || '',
      mileage_km: item.mileage_km?.toString() || '',
      hours: item.hours?.toString() || '',
      has_hours: item.has_hours || false,
      price: item.price?.toString() || '',
      axle_count: item.axle_count?.toString() || '',
      reefer_hours: item.reefer_hours?.toString() || '',
      images: item.images || [],
      video_url: item.video_url || '',
      vin: item.vin || '',
    });
    setEditId(item.id);
    setDialogOpen(true);
  };

  const handleVinDecode = async () => {
    if (!form.vin || form.vin.length < 11) { toast.error('Enter a valid VIN'); return; }
    setDecoding(true);
    try {
      const r = await inventoryAPI.vinDecode(form.vin);
      setForm(f => ({
        ...f,
        make: r.data.make || f.make,
        model: r.data.model || f.model,
        year: r.data.year?.toString() || f.year,
        body_type: r.data.body_type || f.body_type,
        engine: r.data.engine || f.engine,
        horsepower: r.data.horsepower || f.horsepower,
        transmission: r.data.transmission || f.transmission,
        axles: r.data.axles || f.axles,
        gvwr: r.data.gvwr || f.gvwr,
      }));
      toast.success('VIN decoded successfully!');
    } catch { toast.error('VIN decode failed. You can fill in manually.'); }
    setDecoding(false);
  };

  // Auto-generate stock number preview from VIN
  const stockPreview = form.vin && form.vin.length >= 6
    ? `CTS-${form.vin.slice(-6).toUpperCase()}`
    : 'CTS-XXXXXX';

  const generateDescription = (f) => {
    const parts = [];
    const title = `${f.year || ''} ${f.make || ''} ${f.type === 'reefer' ? (f.reefer_unit || f.model || '') : (f.model || '')}`.trim();
    if (title) parts.push(title);
    if (f.engine) parts.push(`equipped with a ${f.engine} engine`);
    if (f.horsepower) parts.push(`producing ${f.horsepower} HP`);
    if (f.transmission) parts.push(`${f.transmission} transmission`);
    if (f.mileage_km && !['dry_van', 'flatbed'].includes(f.type)) parts.push(`${Number(f.mileage_km).toLocaleString()} KM`);
    if (['trailer', 'reefer'].includes(f.type) && f.has_hours && f.hours) parts.push(`${Number(f.hours).toLocaleString()} hours`);
    if (f.type === 'reefer' && f.reefer_hours) parts.push(`${Number(f.reefer_hours).toLocaleString()} reefer hours`);
    if (f.suspension) parts.push(`${f.suspension} suspension`);
    if (f.axles) parts.push(`${f.axles} axles`);
    if (f.wheelbase) parts.push(`${f.wheelbase} wheelbase`);
    if (f.rear_ratio) parts.push(`${f.rear_ratio} rear ratio`);
    if (f.sleeper_daycab) parts.push(f.sleeper_daycab);
    if (f.color) parts.push(f.color);
    if (f.floor_type) parts.push(`${f.floor_type} floor`);
    if (f.length || f.trailer_length) parts.push(`${f.length || f.trailer_length} length`);
    if (f.door_type) parts.push(`${f.door_type} doors`);
    if (f.roof_type) parts.push(`${f.roof_type} roof`);
    if (f.trailer_type) parts.push(`${f.trailer_type}`);
    if (f.condition) parts.push(`${f.condition} condition`);
    parts.push(`Located in ${f.location || 'Mississauga, ON'}. Contact Canadian Truck Sales for pricing.`);
    return parts.join('. ').replace(/\.\./g, '.').replace(/\. \./g, '.');
  };

  const [aiLoading, setAiLoading] = useState(false);

  const autoFillTitleDesc = async () => {
    setAiLoading(true);
    try {
      const res = await inventoryAPI.generateDescription(form);
      setForm(f => ({ ...f, title: res.data.title, description: res.data.description }));
      toast.success('AI description generated!');
    } catch {
      // Fallback to simple generation
      setForm(f => ({
        ...f,
        title: `${f.year || ''} ${f.make || ''} ${f.model || ''}`.trim(),
        description: generateDescription(f),
      }));
      toast.success('Title & description generated');
    }
    setAiLoading(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      // Convert numeric fields: empty string → null, string → number
      payload.year = payload.year ? parseInt(payload.year) : null;
      payload.mileage_km = payload.mileage_km ? parseInt(payload.mileage_km) : null;
      payload.hours = payload.hours ? parseInt(payload.hours) : null;
      payload.price = payload.price ? parseFloat(payload.price) : null;
      payload.axle_count = payload.axle_count ? parseInt(payload.axle_count) : null;
      payload.reefer_hours = payload.reefer_hours ? parseInt(payload.reefer_hours) : null;
      // Clean empty strings to null for optional string fields
      for (const key of Object.keys(payload)) {
        if (payload[key] === '' && key !== 'type' && key !== 'make' && key !== 'model' && key !== 'condition' && key !== 'status' && key !== 'location') {
          payload[key] = null;
        }
      }
      if (!payload.title) payload.title = `${payload.year || ''} ${payload.make} ${payload.model}`.trim();

      if (editId) {
        await inventoryAPI.update(editId, payload);
        toast.success('Unit updated');
      } else {
        await inventoryAPI.create(payload);
        toast.success('Unit added — all clients notified!');
      }
      setDialogOpen(false);
      load();
    } catch (err) { toast.error('Save failed'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this unit?')) return;
    try { await inventoryAPI.delete(id); toast.success('Deleted'); load(); } catch { toast.error('Delete failed'); }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      try {
        const res = await uploadAPI.upload('inventory', file);
        setForm(f => ({ ...f, images: [...f.images, res.data.url] }));
        toast.success(`${file.name} uploaded`);
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
  };

  const removeImage = (idx) => setForm(f => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const handleMatchClients = async (id) => {
    try {
      const res = await notificationsAPI.matchInventory(id);
      if (res.data.matched_clients > 0) {
        toast.success(`Matched ${res.data.matched_clients} client(s)! Notifications created.`);
      } else {
        toast.info('No matching client preferences found.');
      }
    } catch { toast.error('Match failed'); }
  };

  const handleAddCustomStatus = async () => {
    if (!customStatusInput.trim()) return;
    try {
      const r = await inventoryAPI.addStatus(customStatusInput.trim());
      setStatuses(r.data.statuses || statuses);
      setForm(f => ({ ...f, status: customStatusInput.trim() }));
      setCustomStatusInput('');
      toast.success(`Status "${customStatusInput.trim()}" added`);
    } catch { toast.error('Failed to add status'); }
  };

  // Send Listing handlers
  const openSendListing = (item) => {
    setSendListingItem(item);
    setSelectedClientIds([]);
    setCustomEmails('');
    setSendChannels(['email']);
    setClientSearch('');
    setSendListingOpen(true);
  };

  const toggleClientSelection = (clientId) => {
    setSelectedClientIds(prev =>
      prev.includes(clientId) ? prev.filter(id => id !== clientId) : [...prev, clientId]
    );
  };

  const toggleChannel = (ch) => {
    setSendChannels(prev =>
      prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]
    );
  };

  const handleSendListing = async () => {
    if (selectedClientIds.length === 0 && !customEmails.trim()) {
      toast.error('Select at least one client or enter an email');
      return;
    }
    if (sendChannels.length === 0) {
      toast.error('Select at least one channel');
      return;
    }
    setSendingListing(true);
    try {
      const emails = customEmails.split(',').map(e => e.trim()).filter(Boolean);
      const res = await inventoryAPI.sendListing(sendListingItem.id, {
        client_ids: selectedClientIds,
        custom_emails: emails,
        channels: sendChannels,
      });
      const sentList = res.data.sent_to || [];
      const errors = sentList.filter(s => s.error);
      if (errors.length > 0 && errors.length === sentList.length) {
        toast.error(`Send failed: ${errors[0].error}`);
      } else if (errors.length > 0) {
        toast.warning(`Sent to ${sentList.length - errors.length} recipient(s), ${errors.length} failed: ${errors[0].error}`);
      } else {
        toast.success(res.data.message);
      }
      setSendListingOpen(false);
    } catch { toast.error('Failed to send listing'); }
    setSendingListing(false);
  };

  // Send Document handlers
  const openSendDoc = (item) => {
    setSendDocItem(item);
    setDocType('quote');
    setDocClientId('');
    setDocCustomEmail('');
    setDocNotes('');
    setDocQuantity('1');
    setDocUnitPrice(item?.price ? String(item.price) : '');
    setDocSafetyCertified(false);
    setDocValidity('7 days');
    setDocTaxProvince('ON');
    setDocTaxRate('13');
    setDocHoldDeposit('');
    setDocAdminFee('');
    setSendDocOpen(true);
  };

  const handleSendDoc = async () => {
    if (!docClientId && !docCustomEmail.trim()) {
      toast.error('Select a client or enter an email');
      return;
    }
    const qty = Math.max(1, parseInt(docQuantity, 10) || 1);
    const unitPrice = parseFloat(docUnitPrice);
    if (!unitPrice || unitPrice <= 0) {
      toast.error('Enter a unit price');
      return;
    }
    setSendingDoc(true);
    try {
      const res = await inventoryAPI.sendDocument(sendDocItem.id, {
        doc_type: docType,
        client_id: docClientId || null,
        custom_email: docCustomEmail.trim() || null,
        notes: docNotes || null,
        quantity: qty,
        unit_price: unitPrice,
        admin_fee: parseFloat(docAdminFee) || 0,
        safety_certified: docSafetyCertified,
        validity: docValidity || '7 days',
        tax_rate: parseFloat(docTaxRate) || 13,
        tax_province: docTaxProvince || 'ON',
        hold_deposit: parseFloat(docHoldDeposit) || 0,
      });
      toast.success(res.data.message);
      setSendDocOpen(false);
      setDocAdminFee('');
      setDocHoldDeposit('');
    } catch { toast.error('Failed to send document'); }
    setSendingDoc(false);
  };

  // Multi-Unit Quote handlers
  const openMultiQuote = () => {
    if (selected.length < 2) {
      toast.error('Select at least 2 units for a multi-unit quote');
      return;
    }
    const prices = {};
    selected.forEach(id => {
      const item = items.find(i => i.id === id);
      if (item) prices[id] = item.price?.toString() || '';
    });
    setMultiQuotePrices(prices);
    setMultiQuoteClientId('');
    setMultiQuoteEmail('');
    setMultiQuoteNotes('');
    setMultiQuoteAdminFee('');
    setMultiQuoteHst('ON');
    setMultiQuoteHstRate('13');
    setMultiQuoteHoldDeposit('');
    setMultiQuoteOpen(true);
  };

  const selectedItems = useMemo(() => {
    return selected.map(id => items.find(i => i.id === id)).filter(Boolean);
  }, [selected, items]);

  const multiQuoteSubtotal = useMemo(() => {
    return selectedItems.reduce((sum, item) => sum + (parseFloat(multiQuotePrices[item.id]) || 0), 0);
  }, [selectedItems, multiQuotePrices]);

  const handleSendMultiQuote = async () => {
    if (!multiQuoteClientId && !multiQuoteEmail.trim()) {
      toast.error('Select a client or enter an email');
      return;
    }
    if (selectedItems.length < 2) {
      toast.error('Need at least 2 units');
      return;
    }
    setSendingMultiQuote(true);
    try {
      const lineItems = selectedItems.map(item => ({
        description: item.title || `${item.year} ${item.make} ${item.model}`,
        amount: parseFloat(multiQuotePrices[item.id]) || item.price || 0,
      }));
      const adminFeeVal = parseFloat(multiQuoteAdminFee) || 0;
      if (adminFeeVal > 0) {
        lineItems.push({ description: 'Administration / Doc Fee', amount: adminFeeVal });
      }
      const primaryId = selectedItems[0].id;
      const additionalIds = selectedItems.slice(1).map(i => i.id);
      const res = await inventoryAPI.sendDocument(primaryId, {
        doc_type: 'quote',
        client_id: multiQuoteClientId || null,
        custom_email: multiQuoteEmail.trim() || null,
        notes: multiQuoteNotes || null,
        items: lineItems,
        additional_item_ids: additionalIds,
        tax_rate: parseFloat(multiQuoteHstRate) || 13,
        tax_province: multiQuoteHst || 'ON',
        hold_deposit: parseFloat(multiQuoteHoldDeposit) || 0,
      });
      toast.success(res.data.message);
      setMultiQuoteOpen(false);
      setSelected([]);
    } catch { toast.error('Failed to send multi-unit quote'); }
    setSendingMultiQuote(false);
  };

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Photo lightbox handlers
  const openPhotos = (item) => { setPhotosItem(item); setPhotoIdx(0); };
  const closePhotos = () => setPhotosItem(null);

  // Inventory document handlers
  const openDocsDialog = async (item) => {
    setDocsDialogItem(item);
    setLoadingDocs(true);
    try {
      const res = await inventoryAPI.listDocs(item.id);
      setInvDocs(res.data || []);
    } catch { toast.error('Failed to load documents'); }
    setLoadingDocs(false);
  };

  const handleInvDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !docsDialogItem) return;
    setUploadingInvDoc(true);
    try {
      const res = await inventoryAPI.uploadDoc(docsDialogItem.id, file, invDocType);
      setInvDocs(prev => [res.data, ...prev]);
      toast.success('Document uploaded');
    } catch { toast.error('Upload failed'); }
    setUploadingInvDoc(false);
    e.target.value = '';
  };

  const handleDeleteInvDoc = async (docId) => {
    if (!docsDialogItem || !window.confirm('Delete this document?')) return;
    try {
      await inventoryAPI.deleteDoc(docsDialogItem.id, docId);
      setInvDocs(prev => prev.filter(d => d.id !== docId));
      toast.success('Document deleted');
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div data-testid="admin-inventory">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-900 uppercase">Inventory</h1>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">{filteredItems.length} of {items.length} units</span>
          {selected.length >= 2 && (
            <Button className="bg-[#2D3748] text-white hover:bg-[#1A202C]" onClick={openMultiQuote} data-testid="multi-unit-quote-btn">
              <FileText className="w-4 h-4 mr-1" /> Multi-Unit Quote ({selected.length})
            </Button>
          )}
          {isAdmin && (
          <Button className="bg-[#E31937] text-white hover:bg-[#C4122C]" onClick={openNew} data-testid="add-unit-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Unit
          </Button>
        )}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 mb-4 overflow-hidden">
        <div className="flex flex-wrap gap-2 sm:gap-3 items-center">
          <div className="relative w-full sm:flex-1 sm:min-w-[200px]">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by title, VIN, stock #..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && load()}
              className="pl-9 h-9 text-sm w-full"
              data-testid="admin-search"
            />
          </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[calc(50%-4px)] sm:w-[130px] h-9 text-xs sm:text-sm" data-testid="type-filter">
                <Filter className="w-3 h-3 mr-1 text-gray-400 hidden sm:block" /><SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {EQUIPMENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[calc(50%-4px)] sm:w-[120px] h-9 text-xs sm:text-sm" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={makeFilter} onValueChange={setMakeFilter}>
              <SelectTrigger className="w-[calc(50%-4px)] sm:w-[120px] h-9 text-xs sm:text-sm" data-testid="make-filter">
                <SelectValue placeholder="Make" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Makes</SelectItem>
                {[...new Set(items.map(i => i.make).filter(Boolean))].sort().map(m => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              {(typeFilter !== 'all' || statusFilter !== 'all' || makeFilter !== 'all') && (
                <Button variant="ghost" size="sm" onClick={() => { setTypeFilter('all'); setStatusFilter('all'); setMakeFilter('all'); }} className="h-9 text-xs text-gray-500 px-2">
                  <X className="w-3 h-3" />
                </Button>
              )}
              <Button variant="outline" className="border-gray-200 text-gray-600 h-9 text-xs sm:text-sm" onClick={load}>Search</Button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-gray-200 hover:bg-transparent">
              <TableHead className="w-10"><BulkSelectCheckbox checked={selected.length === filteredItems.length && filteredItems.length > 0} onCheckedChange={toggleAll} testId="select-all-inv" /></TableHead>
              <TableHead className="text-gray-500">Stock #</TableHead>
              <TableHead className="text-gray-500 hidden sm:table-cell">VIN</TableHead>
              <TableHead className="text-gray-500">Title</TableHead>
              <TableHead className="text-gray-500 hidden md:table-cell">Type</TableHead>
              <TableHead className="text-gray-500 hidden lg:table-cell">Mileage</TableHead>
              <TableHead className="text-gray-500">Price</TableHead>
              <TableHead className="text-gray-500 hidden md:table-cell text-center">Views</TableHead>
              <TableHead className="text-gray-500 hidden md:table-cell text-center">Photos</TableHead>
              <TableHead className="text-gray-500 hidden lg:table-cell text-center">Docs</TableHead>
              <TableHead className="text-gray-500">Status</TableHead>
              <TableHead className="text-gray-500 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={12} className="text-center text-gray-400 py-8">Loading...</TableCell></TableRow>
            ) : filteredItems.length === 0 ? (
              <TableRow><TableCell colSpan={12} className="text-center text-gray-400 py-8">{(typeFilter !== 'all' || statusFilter !== 'all' || makeFilter !== 'all') ? 'No inventory matches your filters' : 'No inventory found'}</TableCell></TableRow>
            ) : filteredItems.map(item => (
              <TableRow key={item.id} className="border-gray-200 text-gray-700" data-testid={`admin-row-${item.id}`}>
                <TableCell><BulkSelectCheckbox checked={selected.includes(item.id)} onCheckedChange={() => toggleSelect(item.id)} testId={`select-${item.id}`} /></TableCell>
                <TableCell className="text-xs font-mono">{item.stock_number}</TableCell>
                <TableCell className="text-xs font-mono text-gray-500 hidden sm:table-cell max-w-[160px]" title={item.vin || ''} data-testid={`vin-${item.id}`}>{item.vin || '--'}</TableCell>
                <TableCell className="font-medium max-w-[120px] sm:max-w-none truncate">{item.title}</TableCell>
                <TableCell className="hidden md:table-cell"><Badge variant="outline" className="text-xs capitalize border-gray-300 text-gray-700">{TYPE_LABELS[item.type] || item.type}</Badge></TableCell>
                <TableCell className="text-xs text-gray-500 hidden lg:table-cell">{item.has_hours && item.hours ? `${Number(item.hours).toLocaleString()} hrs` : item.mileage_km ? `${Number(item.mileage_km).toLocaleString()} km` : '--'}</TableCell>
                <TableCell>{formatPrice(item.price)}</TableCell>
                <TableCell className="hidden md:table-cell text-center">
                  <span className="inline-flex items-center gap-1 text-xs text-gray-500" data-testid={`views-${item.id}`}>
                    <Eye className="w-3 h-3" />{item.views || 0}
                  </span>
                </TableCell>
                <TableCell className="hidden md:table-cell text-center">
                  {(item.images?.length || 0) > 0 ? (
                    <button
                      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                      onClick={() => openPhotos(item)}
                      data-testid={`photos-${item.id}`}
                    >
                      <Image className="w-3 h-3" />{item.images.length}
                    </button>
                  ) : (
                    <span className="text-xs text-gray-400">0</span>
                  )}
                </TableCell>
                <TableCell className="hidden lg:table-cell text-center">
                  <button
                    className="inline-flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 hover:underline cursor-pointer"
                    onClick={() => openDocsDialog(item)}
                    data-testid={`docs-${item.id}`}
                  >
                    <FolderOpen className="w-3 h-3" />View
                  </button>
                </TableCell>
                <TableCell>
                  <Badge className={
                    item.status === 'Available' ? 'bg-green-100 text-green-800 border border-green-300' :
                    item.status === 'Hold' ? 'bg-amber-100 text-amber-800 border border-amber-300' :
                    item.status === 'Sold' ? 'bg-gray-100 text-gray-700 border border-gray-300' :
                    item.status === 'Coming Soon' ? 'bg-purple-100 text-purple-800 border border-purple-300' :
                    'bg-blue-100 text-blue-800 border border-blue-300'
                  }>
                    {item.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {/* Desktop actions */}
                  <div className="hidden sm:flex items-center justify-end gap-0.5">
                    {isAdmin && (
                      <Button variant="ghost" size="sm" onClick={() => openEdit(item)} title="Edit" data-testid={`edit-${item.id}`}><Pencil className="w-3 h-3" /></Button>
                    )}
                    <Button variant="ghost" size="sm" className="text-blue-500" onClick={() => openSendListing(item)} title="Send Listing" data-testid={`send-listing-${item.id}`}><Send className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="sm" className="text-purple-500" onClick={() => openSendDoc(item)} title="Send Document" data-testid={`send-doc-${item.id}`}><FileText className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="sm" className="text-teal-600" onClick={() => setQrItem(item)} title="QR Sticker" data-testid={`qr-sticker-${item.id}`}><QrCode className="w-3 h-3" /></Button>
                    {isAdmin && (
                      <>
                        <Button variant="ghost" size="sm" className="text-amber-500" onClick={() => handleMatchClients(item.id)} title="Match Preferences" data-testid={`match-${item.id}`}><Bell className="w-3 h-3" /></Button>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(item.id)} title="Delete" data-testid={`delete-${item.id}`}><Trash2 className="w-3 h-3" /></Button>
                      </>
                    )}
                  </div>
                  {/* Mobile dropdown */}
                  <div className="sm:hidden">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`actions-menu-${item.id}`}><MoreVertical className="w-4 h-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-white">
                        {isAdmin && (
                          <DropdownMenuItem onClick={() => openEdit(item)}><Pencil className="w-3.5 h-3.5 mr-2" />Edit</DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => openSendListing(item)}><Send className="w-3.5 h-3.5 mr-2 text-blue-500" />Send Listing</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openSendDoc(item)}><FileText className="w-3.5 h-3.5 mr-2 text-purple-500" />Send Document</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setQrItem(item)}><QrCode className="w-3.5 h-3.5 mr-2 text-teal-600" />QR Sticker</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openPhotos(item)}><Image className="w-3.5 h-3.5 mr-2 text-blue-500" />View Photos</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openDocsDialog(item)}><FolderOpen className="w-3.5 h-3.5 mr-2 text-purple-500" />Documents</DropdownMenuItem>
                        {isAdmin && (
                          <>
                            <DropdownMenuItem onClick={() => handleMatchClients(item.id)}><Bell className="w-3.5 h-3.5 mr-2 text-amber-500" />Match Clients</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleDelete(item.id)} className="text-red-500"><Trash2 className="w-3.5 h-3.5 mr-2" />Delete</DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white border-gray-200 text-gray-900" data-testid="inventory-dialog">
          <DialogHeader>
            <DialogTitle className="text-gray-900 uppercase">{editId ? 'Edit Unit' : 'Add New Unit'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-5">

            {/* SECTION: Equipment Type Selector */}
            <div className="border border-gray-200 rounded-lg p-4" data-testid="equipment-type-section">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Equipment Type</p>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {EQUIPMENT_TYPES.map(t => (
                  <button
                    key={t.value}
                    type="button"
                    className={`px-3 py-2 rounded-lg border text-xs font-medium transition-all ${form.type === t.value ? 'border-[#E31937] bg-[#E31937]/10 text-[#E31937]' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                    onClick={() => update('type', t.value)}
                    data-testid={`type-btn-${t.value}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* SECTION: VIN & Identification */}
            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Identification</p>
              <div className="flex gap-2">
                <Input value={form.vin} onChange={e => update('vin', e.target.value)} placeholder={form.type === 'other' ? 'VIN / Serial Number' : 'Enter VIN to auto-fill'} className="bg-gray-50 border-gray-200" data-testid="vin-input" />
                {form.type !== 'other' && (
                  <Button type="button" variant="outline" className="border-gray-300 text-gray-600 shrink-0" onClick={handleVinDecode} disabled={decoding} data-testid="vin-decode-btn">
                    {decoding ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Decode VIN'}
                  </Button>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Stock #: <span className="font-mono font-semibold text-gray-600" data-testid="stock-preview">{stockPreview}</span>
                {form.vin && form.vin.length >= 6 && <span className="text-green-500 ml-2">(auto-generated from VIN)</span>}
              </p>
            </div>

            {/* SECTION: Basic Info */}
            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Basic Info</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {form.type === 'other' ? (
                  <div className="sm:col-span-2 md:col-span-3">
                    <label className="text-xs text-gray-500">Title / Equipment Name *</label>
                    <Input value={form.title} onChange={e => update('title', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. Generator Set, Lift Gate" data-testid="inv-title" />
                  </div>
                ) : form.type === 'reefer' ? (
                  <>
                    <div><label className="text-xs text-gray-500">Make *</label><Input value={form.make} onChange={e => update('make', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" data-testid="inv-make" /></div>
                    <div><label className="text-xs text-gray-500">Reefer Unit *</label><Input value={form.reefer_unit} onChange={e => update('reefer_unit', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. Carrier X4 7300" data-testid="inv-reefer-unit" /></div>
                  </>
                ) : (
                  <>
                    <div><label className="text-xs text-gray-500">Make *</label><Input value={form.make} onChange={e => update('make', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" data-testid="inv-make" /></div>
                    <div><label className="text-xs text-gray-500">Model *</label><Input value={form.model} onChange={e => update('model', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" data-testid="inv-model" /></div>
                  </>
                )}
                <div><label className="text-xs text-gray-500">Year</label><Input type="number" value={form.year} onChange={e => update('year', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" data-testid="inv-year" /></div>
                <div><label className="text-xs text-gray-500">Condition</label>
                  <Select value={form.condition} onValueChange={v => update('condition', v)}>
                    <SelectTrigger className="bg-gray-50 border-gray-200 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="New">New</SelectItem>
                      <SelectItem value="Used">Used</SelectItem>
                      <SelectItem value="Certified">Certified</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* Mileage: shown for trucks */}
                {form.type === 'truck' && (
                  <div><label className="text-xs text-gray-500">Mileage (KM)</label><Input type="number" value={form.mileage_km} onChange={e => update('mileage_km', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" /></div>
                )}
                {/* Hours/Mileage toggle: for trailer, reefer only */}
                {['trailer', 'reefer'].includes(form.type) && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs text-gray-500">{form.has_hours ? 'Hours' : 'Mileage (KM)'}</label>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <span className="text-[10px] text-gray-400">Hours</span>
                        <Switch checked={form.has_hours} onCheckedChange={v => update('has_hours', v)} data-testid="hours-toggle" />
                      </label>
                    </div>
                    {form.has_hours ? (
                      <Input type="number" placeholder="Hours" value={form.hours} onChange={e => update('hours', e.target.value)} className="bg-gray-50 border-gray-200" data-testid="inv-hours" />
                    ) : (
                      <Input type="number" placeholder="Mileage (KM)" value={form.mileage_km} onChange={e => update('mileage_km', e.target.value)} className="bg-gray-50 border-gray-200" />
                    )}
                  </div>
                )}
                {/* Mileage only for dry_van, flatbed */}
                {['dry_van', 'flatbed'].includes(form.type) && (
                  <div><label className="text-xs text-gray-500">Mileage (KM)</label><Input type="number" value={form.mileage_km} onChange={e => update('mileage_km', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" /></div>
                )}
                <div><label className="text-xs text-gray-500">Location</label><Input value={form.location} onChange={e => update('location', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" /></div>
              </div>
            </div>

            {/* SECTION: Specifications - Dynamic per type */}
            {form.type !== 'other' && (
            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Specifications</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {/* TRUCK specs */}
                {form.type === 'truck' && (
                  <>
                    <div><label className="text-xs text-gray-500">Engine</label><Input value={form.engine} onChange={e => update('engine', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. Cummins X15" /></div>
                    <div><label className="text-xs text-gray-500">Horsepower</label><Input value={form.horsepower} onChange={e => update('horsepower', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. 500" /></div>
                    <div><label className="text-xs text-gray-500">Ratio</label><Input value={form.rear_ratio} onChange={e => update('rear_ratio', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. 3.36" /></div>
                    <div><label className="text-xs text-gray-500">Axle</label><Input value={form.axles} onChange={e => update('axles', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. Tandem" /></div>
                    <div><label className="text-xs text-gray-500">Suspension</label><Input value={form.suspension} onChange={e => update('suspension', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. Air Ride" /></div>
                    <div><label className="text-xs text-gray-500">Wheelbase</label><Input value={form.wheelbase} onChange={e => update('wheelbase', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. 228" /></div>
                    <div><label className="text-xs text-gray-500">GVWR</label><Input value={form.gvwr} onChange={e => update('gvwr', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. 52000 lbs" /></div>
                    <div><label className="text-xs text-gray-500">Transmission</label><Input value={form.transmission} onChange={e => update('transmission', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. 13 Speed" /></div>
                    <div><label className="text-xs text-gray-500">Sleeper / Day Cab</label>
                      <Select value={form.sleeper_daycab || ''} onValueChange={v => update('sleeper_daycab', v)}>
                        <SelectTrigger className="bg-gray-50 border-gray-200 mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Sleeper">Sleeper</SelectItem>
                          <SelectItem value="Day Cab">Day Cab</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><label className="text-xs text-gray-500">Color</label><Input value={form.color} onChange={e => update('color', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. White" /></div>
                  </>
                )}

                {/* TRAILER specs */}
                {form.type === 'trailer' && (
                  <>
                    <div><label className="text-xs text-gray-500">Trailer Type</label><Input value={form.trailer_type} onChange={e => update('trailer_type', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. Enclosed, Open" /></div>
                    <div><label className="text-xs text-gray-500">Axle</label><Input value={form.axles} onChange={e => update('axles', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. Tandem" /></div>
                    <div><label className="text-xs text-gray-500">Suspension</label><Input value={form.suspension} onChange={e => update('suspension', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. Air Ride" /></div>
                    <div><label className="text-xs text-gray-500">Floor Type</label><Input value={form.floor_type} onChange={e => update('floor_type', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. Wood, Aluminum" /></div>
                    <div><label className="text-xs text-gray-500">Length</label><Input value={form.length || form.trailer_length} onChange={e => { update('length', e.target.value); update('trailer_length', e.target.value); }} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. 53'" /></div>
                    <div><label className="text-xs text-gray-500">Width</label><Input value={form.width} onChange={e => update('width', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. 102 in" /></div>
                  </>
                )}

                {/* DRY VAN specs */}
                {form.type === 'dry_van' && (
                  <>
                    <div><label className="text-xs text-gray-500">Floor Type</label><Input value={form.floor_type} onChange={e => update('floor_type', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. Wood, Aluminum" /></div>
                    <div><label className="text-xs text-gray-500">Length</label><Input value={form.length || form.trailer_length} onChange={e => { update('length', e.target.value); update('trailer_length', e.target.value); }} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. 53'" /></div>
                    <div><label className="text-xs text-gray-500">Width</label><Input value={form.width} onChange={e => update('width', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. 102 in" /></div>
                    <div><label className="text-xs text-gray-500">Axle</label><Input value={form.axles} onChange={e => update('axles', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. Tandem" /></div>
                    <div><label className="text-xs text-gray-500">Suspension</label><Input value={form.suspension} onChange={e => update('suspension', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. Air Ride" /></div>
                    <div><label className="text-xs text-gray-500">Roof Type</label><Input value={form.roof_type} onChange={e => update('roof_type', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. Translucent, Steel" /></div>
                    <div><label className="text-xs text-gray-500">Door Type</label><Input value={form.door_type} onChange={e => update('door_type', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. Swing, Roll-Up" /></div>
                  </>
                )}

                {/* REEFER specs (without Reefer Unit and Hours - those are in Basic Info) */}
                {form.type === 'reefer' && (
                  <>
                    <div><label className="text-xs text-gray-500">Reefer Hours</label><Input type="number" value={form.reefer_hours} onChange={e => update('reefer_hours', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. 12000" /></div>
                    <div><label className="text-xs text-gray-500">Axle</label><Input value={form.axles} onChange={e => update('axles', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. Tandem" /></div>
                    <div><label className="text-xs text-gray-500">Floor Type</label><Input value={form.floor_type} onChange={e => update('floor_type', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. Aluminum" /></div>
                    <div><label className="text-xs text-gray-500">Length</label><Input value={form.length || form.trailer_length} onChange={e => { update('length', e.target.value); update('trailer_length', e.target.value); }} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. 53'" /></div>
                    <div><label className="text-xs text-gray-500">Width</label><Input value={form.width} onChange={e => update('width', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. 102 in" /></div>
                    <div><label className="text-xs text-gray-500">Suspension</label><Input value={form.suspension} onChange={e => update('suspension', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. Air Ride" /></div>
                    <div><label className="text-xs text-gray-500">Door Type</label><Input value={form.door_type} onChange={e => update('door_type', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. Swing, Roll-Up" /></div>
                  </>
                )}

                {/* FLATBED specs */}
                {form.type === 'flatbed' && (
                  <>
                    <div><label className="text-xs text-gray-500">Axle</label><Input value={form.axles} onChange={e => update('axles', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. Tandem" /></div>
                    <div><label className="text-xs text-gray-500">Floor Type</label><Input value={form.floor_type} onChange={e => update('floor_type', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. Steel, Aluminum" /></div>
                    <div><label className="text-xs text-gray-500">Length</label><Input value={form.length || form.trailer_length} onChange={e => { update('length', e.target.value); update('trailer_length', e.target.value); }} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. 48'" /></div>
                    <div><label className="text-xs text-gray-500">Width</label><Input value={form.width} onChange={e => update('width', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. 102 in" /></div>
                    <div><label className="text-xs text-gray-500">Suspension</label><Input value={form.suspension} onChange={e => update('suspension', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. Air Ride" /></div>
                  </>
                )}
              </div>
            </div>
            )}

            {/* SECTION: Pricing & Status */}
            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Pricing & Status</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                <div><label className="text-xs text-gray-500">Price ($)</label><Input type="number" value={form.price} onChange={e => update('price', e.target.value)} className="bg-gray-50 border-gray-200 mt-1" /></div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Switch checked={form.price_public} onCheckedChange={v => update('price_public', v)} data-testid="price-public-toggle" />
                    <span className="text-xs text-gray-500">Show price publicly</span>
                  </label>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Status</label>
                  <Select value={form.status} onValueChange={v => update('status', v)}>
                    <SelectTrigger className="bg-gray-50 border-gray-200 mt-1" data-testid="status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-1 mt-1">
                    <Input
                      value={customStatusInput}
                      onChange={e => setCustomStatusInput(e.target.value)}
                      placeholder="Custom status..."
                      className="bg-gray-50 border-gray-200 h-7 text-xs"
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddCustomStatus())}
                      data-testid="custom-status-input"
                    />
                    <Button type="button" size="sm" variant="outline" className="h-7 text-xs px-2 border-gray-200" onClick={handleAddCustomStatus} data-testid="add-status-btn">
                      <Plus className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 text-sm text-gray-600">
                    <input type="checkbox" checked={form.featured} onChange={e => update('featured', e.target.checked)} className="accent-[#E31937]" />
                    Featured
                  </label>
                </div>
              </div>
            </div>

            {/* SECTION: Title & Description */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-gray-500 uppercase">Title & Description</p>
                <Button type="button" variant="ghost" size="sm" className="text-[#E31937] text-xs h-6 gap-1" onClick={autoFillTitleDesc} disabled={aiLoading} data-testid="auto-generate-btn">
                  {aiLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  {aiLoading ? 'Generating...' : 'AI Generate'}
                </Button>
              </div>
              {form.type !== 'other' && (
                <div className="mb-2">
                  <Input value={form.title} onChange={e => update('title', e.target.value)} placeholder="Auto-generated if empty" className="bg-gray-50 border-gray-200" data-testid="inv-title" />
                </div>
              )}
              <Textarea value={form.description} onChange={e => update('description', e.target.value)} className="bg-gray-50 border-gray-200" rows={3} placeholder="Listing Description" data-testid="inv-description" />
            </div>

            {/* SECTION: Media */}
            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Media</p>
              <div className="mb-3">
                <label className="text-xs text-gray-500">Video URL</label>
                <Input value={form.video_url} onChange={e => update('video_url', e.target.value)} placeholder="YouTube, Vimeo, or direct .mp4 link" className="bg-gray-50 border-gray-200 mt-1" data-testid="video-url-input" />
                <p className="text-[10px] text-gray-400 mt-1">Shown first in listing gallery.</p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs text-gray-500">Images ({form.images.length})</label>
                  <label className="cursor-pointer text-xs text-[#E31937] hover:underline flex items-center gap-1" data-testid="upload-images-btn">
                    <Upload className="w-3 h-3" /> Upload Images
                    <input type="file" multiple accept="image/*" onChange={handleImageUpload} className="hidden" />
                  </label>
                </div>
                {form.images.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {form.images.map((img, idx) => (
                      <div key={`img-${img}-${idx}`} className="relative w-20 h-14 rounded overflow-hidden border border-gray-200 group">
                        <img src={resolveImageUrl(img)} alt="" className="w-full h-full object-cover" />
                        <button type="button" className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" onClick={() => removeImage(idx)}>
                          <Trash2 className="w-4 h-4 text-gray-900" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <Input placeholder="Or paste image URL" className="bg-gray-50 border-gray-200 mt-2 text-xs" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (e.target.value) { setForm(f => ({ ...f, images: [...f.images, e.target.value] })); e.target.value = ''; } } }} />
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" className="border-gray-300" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#E31937] text-white hover:bg-[#C4122C]" disabled={saving} data-testid="save-unit-btn">
                {saving ? 'Saving...' : editId ? 'Update Unit' : 'Add Unit'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Send Listing Dialog */}
      <Dialog open={sendListingOpen} onOpenChange={setSendListingOpen}>
        <DialogContent className="w-[min(92vw,620px)] max-w-[620px] max-h-[85vh] overflow-y-auto overflow-x-hidden box-border bg-white text-gray-900" data-testid="send-listing-dialog">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Send Listing</DialogTitle>
          </DialogHeader>
          {sendListingItem && (
            <div className="space-y-4 min-w-0">
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-100 min-w-0">
                <p className="font-semibold text-sm truncate">{sendListingItem.title}</p>
                <p className="text-xs text-gray-500 truncate">{sendListingItem.stock_number} &middot; {sendListingItem.year} &middot; {formatPrice(sendListingItem.price)}</p>
              </div>

              {/* Channels */}
              <div>
                <label className="text-xs text-gray-500 uppercase block mb-2">Send via</label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: 'email', icon: Mail, label: 'Email' },
                    { key: 'whatsapp', icon: MessageCircle, label: 'WhatsApp' },
                  ].map(ch => (
                    <button
                      key={ch.key}
                      type="button"
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border transition-colors ${
                        sendChannels.includes(ch.key) ? 'bg-[#E31937] text-white border-[#E31937]' : 'border-gray-200 text-gray-500 hover:border-gray-400'
                      }`}
                      onClick={() => toggleChannel(ch.key)}
                      data-testid={`channel-${ch.key}`}
                    >
                      <ch.icon className="w-3.5 h-3.5" /> {ch.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Client selection */}
              <div className="min-w-0">
                <label className="text-xs text-gray-500 uppercase block mb-2">Select Clients</label>
                <div className="relative mb-2">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <Input
                    placeholder="Search by name, email, or company..."
                    value={clientSearch}
                    onChange={e => setClientSearch(e.target.value)}
                    className="w-full max-w-full pl-9 h-8 text-sm bg-gray-50"
                    data-testid="client-search-input"
                  />
                </div>
                <div className="max-h-40 overflow-y-auto overflow-x-hidden border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {(() => {
                    const q = clientSearch.toLowerCase();
                    const filtered = clients.filter(c =>
                      !q || c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.company?.toLowerCase().includes(q) || c.phone?.includes(q)
                    );
                    return filtered.length === 0 ? (
                      <p className="text-xs text-gray-400 p-3">{clientSearch ? 'No clients match your search' : 'No clients found'}</p>
                    ) : filtered.map(c => (
                      <label key={c.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer min-w-0" data-testid={`client-option-${c.id}`}>
                        <Checkbox
                          checked={selectedClientIds.includes(c.id)}
                          onCheckedChange={() => toggleClientSelection(c.id)}
                          className="shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{c.name}</p>
                          <p className="text-[11px] text-gray-400 truncate">{c.company ? `${c.company} · ` : ''}{c.email} {c.phone && `| ${c.phone}`}</p>
                        </div>
                      </label>
                    ));
                  })()}
                </div>
              </div>

              {/* Custom emails */}
              <div className="min-w-0">
                <label className="text-xs text-gray-500 uppercase block mb-1">Custom Email Addresses</label>
                <Input
                  value={customEmails}
                  onChange={e => setCustomEmails(e.target.value)}
                  placeholder="email1@example.com, email2@example.com"
                  className="w-full max-w-full bg-gray-100 border-gray-200 text-sm"
                  data-testid="custom-emails-input"
                />
                <p className="text-[10px] text-gray-400 mt-0.5">Comma-separated. Send to anyone, even if not in client list.</p>
              </div>

              <Button
                className="w-full max-w-full bg-[#E31937] hover:bg-[#C4122C] text-white"
                onClick={handleSendListing}
                disabled={sendingListing}
                data-testid="confirm-send-listing"
              >
                {sendingListing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                {sendingListing ? 'Sending...' : 'Send Listing'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Document Dialog (Single Unit) */}
      <Dialog open={sendDocOpen} onOpenChange={setSendDocOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto bg-white text-gray-900" data-testid="send-doc-dialog">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Send Document</DialogTitle>
          </DialogHeader>
          {sendDocItem && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                <p className="font-semibold text-sm">{sendDocItem.title}</p>
                <p className="text-xs text-gray-500">{sendDocItem.stock_number} &middot; {formatPrice(sendDocItem.price)}</p>
              </div>

              <div>
                <label className="text-xs text-gray-500 uppercase block mb-1">Document Type</label>
                <Select value={docType} onValueChange={setDocType}>
                  <SelectTrigger className="bg-gray-100 border-gray-200" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DOC_TYPES.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs text-gray-500 uppercase block mb-1">Send to Client</label>
                <Select value={docClientId || 'none'} onValueChange={v => setDocClientId(v === 'none' ? '' : v)}>
                  <SelectTrigger className="bg-gray-100 border-gray-200" data-testid="doc-client-select"><SelectValue placeholder="Select client..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- Select Client --</SelectItem>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name} ({c.email})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs text-gray-500 uppercase block mb-1">Or Custom Email</label>
                <Input value={docCustomEmail} onChange={e => setDocCustomEmail(e.target.value)} placeholder="recipient@email.com" className="bg-gray-100 border-gray-200" data-testid="doc-custom-email" />
              </div>

              <div>
                <label className="text-xs text-gray-500 uppercase block mb-1">Notes (optional)</label>
                <Textarea value={docNotes} onChange={e => setDocNotes(e.target.value)} className="bg-gray-100 border-gray-200" rows={2} placeholder="Add a note..." data-testid="doc-notes" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 uppercase block mb-1">Quantity</label>
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    value={docQuantity}
                    onChange={e => setDocQuantity(e.target.value)}
                    className="bg-gray-100 border-gray-200 text-sm"
                    data-testid="doc-quantity"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase block mb-1">Unit Price ($)</label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={docUnitPrice}
                    onChange={e => setDocUnitPrice(e.target.value)}
                    placeholder="0.00"
                    className="bg-gray-100 border-gray-200 text-sm"
                    data-testid="doc-unit-price"
                  />
                </div>
              </div>

              {/* Live total preview */}
              {(() => {
                const q = Math.max(1, parseInt(docQuantity, 10) || 1);
                const up = parseFloat(docUnitPrice) || 0;
                const total = q * up;
                return (
                  <div className="bg-gray-50 border border-gray-100 rounded-md px-3 py-2 flex justify-between items-center text-xs" data-testid="doc-line-total">
                    <span className="text-gray-500">{q} × ${up.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                    <span className="font-semibold text-gray-900">${total.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  </div>
                );
              })()}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 uppercase block mb-1">Validity</label>
                  <Input
                    value={docValidity}
                    onChange={e => setDocValidity(e.target.value)}
                    placeholder="7 days"
                    className="bg-gray-100 border-gray-200 text-sm"
                    data-testid="doc-validity"
                  />
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer select-none">
                    <Switch
                      checked={docSafetyCertified}
                      onCheckedChange={setDocSafetyCertified}
                      data-testid="doc-safety-certified"
                    />
                    <span>Safety Certified</span>
                  </label>
                </div>
              </div>

              {docType === 'quote' && (
                <div>
                  <label className="text-xs text-gray-500 uppercase block mb-1">Admin / Doc Fee ($)</label>
                  <Input type="number" value={docAdminFee} onChange={e => setDocAdminFee(e.target.value)} placeholder="0.00" className="bg-gray-100 border-gray-200 w-40 text-xs" data-testid="doc-admin-fee" />
                </div>
              )}

              {/* Tax Province & Rate */}
              <div>
                <label className="text-xs text-gray-500 uppercase block mb-1">Tax</label>
                <div className="flex items-center gap-2">
                  <Select value={docTaxProvince} onValueChange={v => { setDocTaxProvince(v); const p = PROVINCE_TAX.find(p => p.code === v); if (p) setDocTaxRate(p.rate.toString()); }}>
                    <SelectTrigger className="bg-gray-100 border-gray-200 flex-1 text-xs" data-testid="doc-tax-province">
                      <span>{PROVINCE_TAX.find(p => p.code === docTaxProvince)?.name || docTaxProvince} ({docTaxRate}%)</span>
                    </SelectTrigger>
                    <SelectContent>
                      {PROVINCE_TAX.map(p => <SelectItem key={p.code} value={p.code}>{p.name} ({p.rate}%)</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input type="number" step="0.1" value={docTaxRate} onChange={e => setDocTaxRate(e.target.value)} className="bg-gray-100 border-gray-200 w-16 text-xs text-right" data-testid="doc-tax-rate" />
                  <span className="text-xs text-gray-500">%</span>
                </div>
              </div>

              {/* Hold Deposit */}
              <div>
                <label className="text-xs text-gray-500 uppercase block mb-1">Hold Deposit ($) <span className="text-[10px] text-gray-400 normal-case">(optional)</span></label>
                <Input type="number" value={docHoldDeposit} onChange={e => setDocHoldDeposit(e.target.value)} placeholder="0.00" className="bg-gray-100 border-gray-200 w-40 text-xs" data-testid="doc-hold-deposit" />
              </div>

              {!docClientId && !docCustomEmail.trim() && (
                <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">Select a client or enter a custom email.</p>
              )}

              <Button className="w-full bg-[#E31937] hover:bg-[#C4122C] text-white" onClick={handleSendDoc} disabled={sendingDoc || (!docClientId && !docCustomEmail.trim())} data-testid="confirm-send-doc">
                {sendingDoc ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileText className="w-4 h-4 mr-1" />}
                {sendingDoc ? 'Sending...' : `Send ${DOC_TYPES.find(d => d.value === docType)?.label || 'Document'}`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Multi-Unit Quote Dialog */}
      <Dialog open={multiQuoteOpen} onOpenChange={setMultiQuoteOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto bg-white text-gray-900" data-testid="multi-quote-dialog">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Multi-Unit Quote ({selectedItems.length} Units)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Selected units with custom price */}
            <div>
              <label className="text-xs text-gray-500 uppercase block mb-2">Selected Units</label>
              <div className="space-y-2">
                {selectedItems.map(item => (
                  <div key={item.id} className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2" data-testid={`mq-unit-${item.id}`}>
                    {item.images?.[0] && (
                      <img src={resolveImageUrl(item.images[0])} alt="" className="w-12 h-9 object-cover rounded" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.title}</p>
                      <p className="text-[10px] text-gray-500">{item.stock_number} &middot; {item.vin}</p>
                    </div>
                    <div className="shrink-0">
                      <label className="text-[10px] text-gray-400 block">Custom Price</label>
                      <div className="flex items-center">
                        <span className="text-xs text-gray-500 mr-1">$</span>
                        <Input
                          type="number"
                          value={multiQuotePrices[item.id] || ''}
                          onChange={e => setMultiQuotePrices(prev => ({ ...prev, [item.id]: e.target.value }))}
                          placeholder={item.price?.toLocaleString() || '0'}
                          className="bg-white border-gray-300 w-28 h-8 text-xs text-right"
                          data-testid={`mq-price-${item.id}`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Financial summary */}
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Equipment Total ({selectedItems.length} units)</span>
                <span className="font-semibold">${multiQuoteSubtotal.toLocaleString('en-CA', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex items-center justify-between">
                <label className="text-sm text-gray-600">Admin / Doc Fee</label>
                <div className="flex items-center">
                  <span className="text-xs text-gray-400 mr-1">$</span>
                  <Input type="number" value={multiQuoteAdminFee} onChange={e => setMultiQuoteAdminFee(e.target.value)} placeholder="0.00" className="bg-white border-gray-300 w-28 h-8 text-xs text-right" data-testid="mq-admin-fee" />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <label className="text-sm text-gray-600">Tax Province & Rate</label>
                <div className="flex items-center gap-2">
                  <Select value={multiQuoteHst} onValueChange={v => { setMultiQuoteHst(v); const p = PROVINCE_TAX.find(p => p.code === v); if (p) setMultiQuoteHstRate(p.rate.toString()); }}>
                    <SelectTrigger className="bg-white border-gray-300 w-36 h-8 text-xs" data-testid="mq-tax-province">
                      <span>{PROVINCE_TAX.find(p => p.code === multiQuoteHst)?.name || multiQuoteHst} ({multiQuoteHstRate}%)</span>
                    </SelectTrigger>
                    <SelectContent>
                      {PROVINCE_TAX.map(p => <SelectItem key={p.code} value={p.code}>{p.name} ({p.rate}%)</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input type="number" step="0.1" value={multiQuoteHstRate} onChange={e => setMultiQuoteHstRate(e.target.value)} className="bg-white border-gray-300 w-16 h-8 text-xs text-right" data-testid="mq-tax-rate" />
                  <span className="text-xs text-gray-500">%</span>
                </div>
              </div>
              <div className="flex justify-between text-sm text-gray-500">
                <span>Tax ({multiQuoteHstRate}% — {multiQuoteHst})</span>
                <span>${((multiQuoteSubtotal + (parseFloat(multiQuoteAdminFee) || 0)) * ((parseFloat(multiQuoteHstRate) || 0) / 100)).toLocaleString('en-CA', { minimumFractionDigits: 2 })}</span>
              </div>
              {(() => {
                const adminFee = parseFloat(multiQuoteAdminFee) || 0;
                const taxRate = parseFloat(multiQuoteHstRate) || 0;
                const holdDep = parseFloat(multiQuoteHoldDeposit) || 0;
                const subtotal = multiQuoteSubtotal + adminFee;
                const taxAmt = subtotal * (taxRate / 100);
                const totalAmount = subtotal + taxAmt;
                const balance = totalAmount - holdDep;
                return (
                  <>
                    <div className="border-t border-gray-300 pt-2 flex justify-between text-sm font-bold">
                      <span>Total Amount</span>
                      <span>${totalAmount.toLocaleString('en-CA', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <label className="text-sm text-gray-600">Hold Deposit <span className="text-[10px] text-gray-400">(optional)</span></label>
                      <div className="flex items-center">
                        <span className="text-xs text-gray-400 mr-1">$</span>
                        <Input type="number" value={multiQuoteHoldDeposit} onChange={e => setMultiQuoteHoldDeposit(e.target.value)} placeholder="0.00" className="bg-white border-gray-300 w-28 h-8 text-xs text-right" data-testid="mq-hold-deposit" />
                      </div>
                    </div>
                    {holdDep > 0 && (
                      <div className="border-t border-gray-300 pt-2 flex justify-between text-sm font-bold">
                        <span className="text-[#E31937]">Balance</span>
                        <span className="text-[#E31937]">${balance.toLocaleString('en-CA', { minimumFractionDigits: 2 })}</span>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Client or email */}
            <div>
              <label className="text-xs text-gray-500 uppercase block mb-1">Send to Client</label>
              <Select value={multiQuoteClientId || 'none'} onValueChange={v => setMultiQuoteClientId(v === 'none' ? '' : v)}>
                <SelectTrigger className="bg-gray-100 border-gray-200" data-testid="mq-client-select"><SelectValue placeholder="Select client..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Select Client --</SelectItem>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name} ({c.email})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase block mb-1">Or Custom Email</label>
              <Input value={multiQuoteEmail} onChange={e => setMultiQuoteEmail(e.target.value)} placeholder="recipient@email.com" className="bg-gray-100 border-gray-200" data-testid="mq-custom-email" />
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase block mb-1">Notes (optional)</label>
              <Textarea value={multiQuoteNotes} onChange={e => setMultiQuoteNotes(e.target.value)} className="bg-gray-100 border-gray-200" rows={2} placeholder="Add a note..." />
            </div>

            {!multiQuoteClientId && !multiQuoteEmail.trim() && (
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">Select a client or enter a custom email.</p>
            )}

            <Button className="w-full bg-[#E31937] hover:bg-[#C4122C] text-white" onClick={handleSendMultiQuote} disabled={sendingMultiQuote || (!multiQuoteClientId && !multiQuoteEmail.trim())} data-testid="confirm-multi-quote">
              {sendingMultiQuote ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileText className="w-4 h-4 mr-1" />}
              {sendingMultiQuote ? 'Sending...' : `Send Multi-Unit Quote (${selectedItems.length} Units)`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <BulkActionBar count={selected.length} onDelete={handleBulkDelete} onClear={() => setSelected([])} label="unit(s)" />

      {/* QR Sticker Dialog */}
      <Dialog open={!!qrItem} onOpenChange={() => setQrItem(null)}>

        <DialogContent className="max-w-sm bg-white text-gray-900" data-testid="qr-sticker-dialog">
          <DialogHeader>
            <DialogTitle className="text-gray-900">QR Code & Flyer</DialogTitle>
          </DialogHeader>
          {qrItem && (
            <div className="space-y-4">
              <div id="qr-sticker-print" className="bg-white border-2 border-gray-300 rounded-lg p-6 flex flex-col items-center gap-4" style={{width: '280px', margin: '0 auto'}}>
                <img src="https://customer-assets.emergentagent.com/job_12492ec0-1e26-4f4b-96d9-e0a655349093/artifacts/4uh3nwhf_image.png" alt="CTS" className="h-12 w-auto" />
                <div className="text-center">
                  <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Stock #: {qrItem.stock_number}</p>
                  {qrItem.vin && <p className="text-xs font-mono text-gray-700 mt-1">VIN: {qrItem.vin}</p>}
                  <p className="text-sm font-bold text-gray-900 mt-1">{qrItem.title}</p>
                </div>
                <div className="bg-white p-2 border border-gray-200 rounded">
                  <QRCode
                    value={`${process.env.REACT_APP_BACKEND_URL}/inventory/${qrItem.id}`}
                    size={180}
                    level="H"
                    data-testid="qr-code-svg"
                  />
                </div>
                <p className="text-[9px] text-gray-400 text-center">Scan to view full details</p>
              </div>
              <div className="flex flex-col gap-2">
                {/* Download Flyer PDF */}
                <Button
                  className="w-full bg-[#E31937] hover:bg-[#C4122C] text-white"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('cts_token');
                      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/inventory/${qrItem.id}/flyer`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      if (!res.ok) throw new Error('Download failed');
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `CTS_Flyer_${qrItem.stock_number}.pdf`;
                      link.click();
                      URL.revokeObjectURL(url);
                    } catch { toast.error('Failed to download flyer'); }
                  }}
                  data-testid="download-flyer-btn"
                >
                  <Download className="w-4 h-4 mr-2" /> Download Flyer PDF
                </Button>
                <div className="flex gap-2">
                  <Button
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700"
                    variant="outline"
                    onClick={() => {
                      const el = document.getElementById('qr-sticker-print');
                      if (!el) return;
                      const win = window.open('', '_blank', 'width=400,height=600');
                      if (!win) return;
                      const doc = win.document;
                      doc.open();
                      const style = doc.createElement('style');
                      style.textContent = `body{margin:0;padding:20px;display:flex;justify-content:center;align-items:flex-start;font-family:Arial,sans-serif;}
                        .sticker{border:2px solid #ccc;border-radius:8px;padding:24px;display:flex;flex-direction:column;align-items:center;gap:16px;width:280px;}
                        .sticker img.logo{height:48px;width:auto;}
                        .info{text-align:center;}
                        .info .stock{font-size:11px;color:#666;text-transform:uppercase;letter-spacing:1px;font-weight:600;}
                        .info .vin{font-size:11px;font-family:monospace;color:#444;margin-top:4px;}
                        .info .title{font-size:14px;font-weight:700;color:#111;margin-top:4px;}
                        .qr-wrap{background:#fff;padding:8px;border:1px solid #ddd;border-radius:4px;}
                        .scan-text{font-size:9px;color:#999;text-align:center;}
                        @media print{body{padding:0;}.sticker{border:2px solid #000;}}`;
                      doc.head.appendChild(style);
                      doc.title = `QR Sticker - ${qrItem.stock_number || ''}`;
                      const container = doc.createElement('div');
                      // XSS hardening: sanitize cloned innerHTML before injecting into the
                      // new print window. SVG profile is enabled so the QR code renders correctly.
                      container.innerHTML = DOMPurify.sanitize(el.innerHTML, {
                        USE_PROFILES: { html: true, svg: true, svgFilters: true },
                      });
                      doc.body.appendChild(container);
                      doc.close();
                      setTimeout(() => { win.print(); }, 500);
                    }}
                    data-testid="print-qr-sticker"
                  >
                    <Printer className="w-4 h-4 mr-2" /> Print QR
                  </Button>
                  <Button
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700"
                    variant="outline"
                    onClick={() => {
                      const el = document.getElementById('qr-sticker-print');
                      const svgEl = el.querySelector('svg');
                      const canvas = document.createElement('canvas');
                      const scale = 3;
                      canvas.width = 320 * scale;
                      canvas.height = 420 * scale;
                      const ctx = canvas.getContext('2d');
                      ctx.scale(scale, scale);
                      ctx.fillStyle = '#fff';
                      ctx.fillRect(0, 0, 320, 420);
                      ctx.strokeStyle = '#ccc';
                      ctx.lineWidth = 2;
                      ctx.roundRect(10, 10, 300, 400, 8);
                      ctx.stroke();
                      const logo = new Image();
                      logo.crossOrigin = 'anonymous';
                      logo.onload = () => {
                        const lw = 120, lh = (logo.height / logo.width) * lw;
                        ctx.drawImage(logo, (320 - lw) / 2, 25, lw, lh);
                        ctx.fillStyle = '#666';
                        ctx.font = '600 10px Arial';
                        ctx.textAlign = 'center';
                        ctx.fillText(`STOCK #: ${qrItem.stock_number}`, 160, 80);
                        if (qrItem.vin) { ctx.font = '10px monospace'; ctx.fillStyle = '#444'; ctx.fillText(`VIN: ${qrItem.vin}`, 160, 96); }
                        ctx.fillStyle = '#111';
                        ctx.font = 'bold 13px Arial';
                        ctx.fillText(qrItem.title || '', 160, qrItem.vin ? 116 : 100);
                        if (svgEl) {
                          const svgData = new XMLSerializer().serializeToString(svgEl);
                          const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
                          const url = URL.createObjectURL(svgBlob);
                          const qrImg = new Image();
                          qrImg.onload = () => {
                            const qy = qrItem.vin ? 130 : 114;
                            ctx.strokeStyle = '#ddd';
                            ctx.lineWidth = 1;
                            ctx.strokeRect(68, qy, 184, 184);
                            ctx.drawImage(qrImg, 70, qy + 2, 180, 180);
                            ctx.fillStyle = '#999';
                            ctx.font = '8px Arial';
                            ctx.fillText('Scan to view full details', 160, qy + 200);
                            const link = document.createElement('a');
                            link.download = `QR-${qrItem.stock_number}.png`;
                            link.href = canvas.toDataURL('image/png');
                            link.click();
                            URL.revokeObjectURL(url);
                          };
                          qrImg.src = url;
                        }
                      };
                      logo.src = 'https://customer-assets.emergentagent.com/job_12492ec0-1e26-4f4b-96d9-e0a655349093/artifacts/4uh3nwhf_image.png';
                    }}
                    data-testid="download-qr-sticker"
                  >
                    <Download className="w-4 h-4 mr-2" /> QR PNG
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Photos Lightbox */}
      {photosItem && photosItem.images?.length > 0 && (
        <Dialog open={!!photosItem} onOpenChange={closePhotos}>
          <DialogContent className="max-w-4xl max-h-[90vh] bg-black/95 border-0 p-0" data-testid="photos-lightbox">
            <div className="relative flex items-center justify-center h-[80vh]">
              <button className="absolute top-3 right-3 z-20 text-white/70 hover:text-white bg-black/30 rounded-full p-2" onClick={closePhotos} data-testid="photos-close">
                <X className="w-5 h-5" />
              </button>
              <div className="absolute top-3 left-3 z-20 text-white/70 text-sm bg-black/30 rounded px-2 py-1">
                {photoIdx + 1} / {photosItem.images.length} &mdash; {photosItem.title || photosItem.stock_number}
              </div>
              {photosItem.images.length > 1 && (
                <>
                  <button className="absolute left-3 top-1/2 -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 text-white p-2 rounded-full" onClick={() => setPhotoIdx(i => (i - 1 + photosItem.images.length) % photosItem.images.length)} data-testid="photos-prev">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button className="absolute right-3 top-1/2 -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 text-white p-2 rounded-full" onClick={() => setPhotoIdx(i => (i + 1) % photosItem.images.length)} data-testid="photos-next">
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
              <img
                src={resolveImageUrl(photosItem.images[photoIdx])}
                alt={`Photo ${photoIdx + 1}`}
                className="max-w-full max-h-[75vh] object-contain rounded"
                data-testid="photos-image"
              />
            </div>
            {/* Thumbnail strip */}
            {photosItem.images.length > 1 && (
              <div className="flex gap-1 p-2 overflow-x-auto justify-center bg-black">
                {photosItem.images.map((img, i) => (
                  <button key={i} onClick={() => setPhotoIdx(i)} className={`flex-shrink-0 w-14 h-10 rounded overflow-hidden border-2 transition-all ${i === photoIdx ? 'border-white' : 'border-transparent opacity-50 hover:opacity-80'}`}>
                    <img src={resolveImageUrl(img)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Inventory Documents Dialog */}
      <Dialog open={!!docsDialogItem} onOpenChange={() => setDocsDialogItem(null)}>
        <DialogContent className="max-w-lg bg-white border-gray-200" data-testid="inv-docs-dialog">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <FolderOpen className="w-4 h-4 text-purple-600" />
              Documents &mdash; {docsDialogItem?.title || docsDialogItem?.stock_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Upload section */}
            <div className="flex items-center gap-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
              <Select value={invDocType} onValueChange={setInvDocType}>
                <SelectTrigger className="w-36 h-8 text-xs bg-white" data-testid="inv-doc-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="warranty">Warranty</SelectItem>
                  <SelectItem value="inspection">Inspection</SelectItem>
                  <SelectItem value="certificate">Certificate</SelectItem>
                  <SelectItem value="registration">Registration</SelectItem>
                  <SelectItem value="insurance">Insurance</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="text-xs" onClick={() => document.getElementById('inv-doc-upload').click()} disabled={uploadingInvDoc} data-testid="inv-doc-upload-btn">
                <Upload className="w-3 h-3 mr-1" /> {uploadingInvDoc ? 'Uploading...' : 'Upload'}
              </Button>
              <input id="inv-doc-upload" type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx" onChange={handleInvDocUpload} />
            </div>

            {/* Documents list */}
            <div className="max-h-[40vh] overflow-y-auto space-y-2">
              {loadingDocs ? (
                <div className="text-center py-6 text-gray-400 text-sm">Loading...</div>
              ) : invDocs.length === 0 ? (
                <div className="text-center py-6 text-gray-400 text-sm">No documents uploaded yet</div>
              ) : invDocs.map(doc => (
                <div key={doc.id} className="flex items-center justify-between gap-2 border border-gray-200 rounded-lg p-2.5 hover:bg-gray-50 group" data-testid={`inv-doc-${doc.id}`}>
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-700 truncate">{doc.original_name}</p>
                      <span className="text-[10px] text-gray-400">
                        <Badge variant="outline" className="text-[9px] mr-1 capitalize border-gray-300">{doc.doc_type}</Badge>
                        {doc.uploaded_by} &middot; {new Date(doc.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${doc.url}`} target="_blank" rel="noreferrer" className="text-blue-500 hover:text-blue-700 p-1" data-testid={`view-doc-${doc.id}`}>
                      <Eye className="w-3.5 h-3.5" />
                    </a>
                    <button onClick={() => handleDeleteInvDoc(doc.id)} className="text-red-400 hover:text-red-600 p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-inv-doc-${doc.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
